import { Menu, MenuList } from "@mui/material";
import { type MuiMenu } from "@shared/frontend/hooks/use-mui-menu.hook";
import { useNotFirstEffect } from "@shared/frontend/hooks/use-not-first-effect.hook";
import React, { useMemo } from "react";
import { useHyperTable } from "../../HyperTable";
import BooleanFilter from "./filter-types/BooleanFilter";
import CustomFilter from "./filter-types/CustomFilter";
import DateFilter from "./filter-types/DateFilter";
import {
  isFieldBoolean,
  isFieldDate,
  isFieldLiteral,
  isFieldNumber,
  isFieldString,
} from "./filter-types/helpers";
import LiteralFilter from "./filter-types/LiteralFilter";
import NumberFilter from "./filter-types/NumberFilter";
import StringFilter from "./filter-types/StringFilter";
import { isCustomFilter, isEntityFilter } from "./FilterMenuItem";
import { useToolbarFilters } from "./HyperTableToolbar";

interface FilterMenuInnerMenuProps<Entity = any> {
  filterId: string;
  menu: MuiMenu<HTMLLIElement>;
}

export const FilterMenuInnerMenu = <Entity extends any>({
  filterId,
  menu: [ref, menuProps, menu],
}: FilterMenuInnerMenuProps<Entity>) => {
  const { filters } = useToolbarFilters<Entity>();
  const {
    filters: activeFilters,
    filterActions: activeFiltersActions,
    getEntity,
  } = useHyperTable<Entity>();

  const thisFilter = filters.find((v) => v.id === filterId);
  const thisActiveFilter = activeFilters.find((v) => v.id === filterId);

  const entity = getEntity();

  const FilterComponent = useMemo(() => {
    if (!thisFilter) return null;
    return isCustomFilter(thisFilter)
      ? CustomFilter
      : isFieldLiteral(entity, thisFilter.fieldName)
        ? LiteralFilter
        : isFieldString(entity, thisFilter.fieldName)
          ? StringFilter
          : isFieldBoolean(entity, thisFilter.fieldName)
            ? BooleanFilter
            : isFieldNumber(entity, thisFilter.fieldName)
              ? NumberFilter
              : isFieldDate(entity, thisFilter.fieldName)
                ? DateFilter
                : null;
  }, [
    entity,
    thisFilter ? isEntityFilter(thisFilter) : undefined,
    (thisFilter as any)?.fieldName,
  ]);

  useNotFirstEffect(() => {
    if (thisActiveFilter && thisFilter) {
      const filter = {
        ...thisActiveFilter,
        ...thisFilter,
      };
      if (isEntityFilter(filter)) {
        filter.onChange?.(thisActiveFilter);
      }
    } else if (thisFilter && "onChange" in thisFilter) {
      thisFilter.onChange?.({
        id: thisFilter.id,
        meta: {
          selectOperatorValue: "contains",
        },
        value: {},
        saveInSessionStorage: true,
      });
    }
  }, [JSON.stringify(thisActiveFilter?.value)]);

  if (!thisFilter || !thisActiveFilter) return null;

  const filter = {
    ...thisActiveFilter,
    ...thisFilter,
  };

  function onClose() {
    menuProps.onClose();
    if (Object.keys(filter.value).length === 0) {
      activeFiltersActions.remove(filter.id);
    }
  }

  return (
    <Menu
      disableAutoFocus
      disableAutoFocusItem
      disableRestoreFocus
      slotProps={{
        paper: {
          sx: {
            display: "flex",
            overflowY: "hidden",
            overflowX: "auto",
            maxHeight: "50vh",
            maxWidth: "85vw",
            padding: 0,
            [".MuiMenu-list"]: {
              flex: "1 1 auto",
            },
          },
        },
        root: {
          onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
        },
      }}
      onClick={(e) => e.stopPropagation()}
      {...menuProps}
      onClose={onClose}
    >
      <MenuList
        disabledItemsFocusable
        sx={{ minWidth: "18rem", height: "100%" }}
        onKeyDown={(e) => {
          if (e.key !== "Escape") {
            e.stopPropagation();
          }
        }}
      >
        {FilterComponent && (
          <FilterComponent
            closeMenu={onClose}
            filter={
              filter as any /* Needs to be as any because ts does not infer that I have already tested isCustomFilter  */
            }
          />
        )}
      </MenuList>
    </Menu>
  );
};
